import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Calculator, Home, RefreshCw } from "lucide-react";

const Calculators = () => {
  // Purchase Calculator State
  const [purchaseData, setPurchaseData] = useState({
    homePrice: '',
    downPayment: '',
    interestRate: '',
    loanTerm: '30',
    propertyTax: '',
    homeInsurance: '',
    pmi: '',
    hoaFees: ''
  });

  // Refinance Calculator State
  const [refinanceData, setRefinanceData] = useState({
    currentPayment: '',
    currentRate: '',
    newRate: '',
    newLoanAmount: '',
    newLoanTerm: '30',
    propertyTax: '',
    homeInsurance: '',
    hoaFees: ''
  });

  // Purchase Calculator Results
  const [purchaseResults, setPurchaseResults] = useState(null);
  
  // Refinance Calculator Results
  const [refinanceResults, setRefinanceResults] = useState(null);

  const calculatePurchase = () => {
    const price = parseFloat(purchaseData.homePrice) || 0;
    const down = parseFloat(purchaseData.downPayment) || 0;
    const rate = parseFloat(purchaseData.interestRate) / 100 / 12;
    const term = parseInt(purchaseData.loanTerm) * 12 || 360;
    const tax = parseFloat(purchaseData.propertyTax) / 12 || 0;
    const insurance = parseFloat(purchaseData.homeInsurance) / 12 || 0;
    const pmi = parseFloat(purchaseData.pmi) || 0;
    const hoa = parseFloat(purchaseData.hoaFees) || 0;

    const loanAmount = price - down;

    // Validation
    if (price <= 0 || rate <= 0 || loanAmount <= 0) {
      alert('Please enter valid home price, down payment, and interest rate');
      return;
    }

    const monthlyPrincipalInterest = loanAmount * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
    const totalMonthlyPayment = monthlyPrincipalInterest + tax + insurance + pmi + hoa;

    setPurchaseResults({
      loanAmount: loanAmount.toFixed(2),
      monthlyPI: monthlyPrincipalInterest.toFixed(2),
      monthlyPITI: totalMonthlyPayment.toFixed(2),
      totalInterest: ((monthlyPrincipalInterest * term) - loanAmount).toFixed(2)
    });
  };

  const calculateRefinance = () => {
    const currentPayment = parseFloat(refinanceData.currentPayment) || 0;
    const newLoanAmount = parseFloat(refinanceData.newLoanAmount) || 0;
    const newRate = parseFloat(refinanceData.newRate) / 100 / 12;
    const newTerm = parseInt(refinanceData.newLoanTerm) * 12 || 360;
    const tax = parseFloat(refinanceData.propertyTax) / 12 || 0;
    const insurance = parseFloat(refinanceData.homeInsurance) / 12 || 0;
    const hoa = parseFloat(refinanceData.hoaFees) || 0;

    // Validation
    if (newLoanAmount <= 0 || newRate <= 0) {
      alert('Please enter valid loan amount and interest rate');
      return;
    }

    const newMonthlyPI = newLoanAmount * (newRate * Math.pow(1 + newRate, newTerm)) / (Math.pow(1 + newRate, newTerm) - 1);
    const newTotalPayment = newMonthlyPI + tax + insurance + hoa;
    const monthlySavings = currentPayment - newTotalPayment;

    setRefinanceResults({
      newMonthlyPI: newMonthlyPI.toFixed(2),
      newTotalPayment: newTotalPayment.toFixed(2),
      monthlySavings: monthlySavings.toFixed(2),
      annualSavings: (monthlySavings * 12).toFixed(2)
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Calculator className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Mortgage Calculators
            </h1>
            <p className="text-xl text-blue-100">
              Plan your homeownership journey with our easy-to-use calculators. 
              Get instant estimates for purchase and refinance scenarios.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <Tabs defaultValue="purchase" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="purchase" className="flex items-center space-x-2">
                <Home className="h-4 w-4" />
                <span>Purchase Calculator</span>
              </TabsTrigger>
              <TabsTrigger value="refinance" className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Refinance Calculator</span>
              </TabsTrigger>
            </TabsList>

            {/* Purchase Calculator */}
            <TabsContent value="purchase">
              <div className="grid lg:grid-cols-2 gap-8">
                <Card className="border-none bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Home className="h-5 w-5 text-[#0b2c4a]" />
                      <span>Purchase Calculator</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="homePrice">Home Price</Label>
                        <Input
                          id="homePrice"
                          type="number"
                          placeholder="500000"
                          value={purchaseData.homePrice}
                          onChange={(e) => setPurchaseData({...purchaseData, homePrice: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="downPayment">Down Payment</Label>
                        <Input
                          id="downPayment"
                          type="number"
                          placeholder="100000"
                          value={purchaseData.downPayment}
                          onChange={(e) => setPurchaseData({...purchaseData, downPayment: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="interestRate">Interest Rate (%)</Label>
                        <Input
                          id="interestRate"
                          type="number"
                          step="0.01"
                          placeholder="6.5"
                          value={purchaseData.interestRate}
                          onChange={(e) => setPurchaseData({...purchaseData, interestRate: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="loanTerm">Loan Term (Years)</Label>
                        <Input
                          id="loanTerm"
                          type="number"
                          placeholder="30"
                          value={purchaseData.loanTerm}
                          onChange={(e) => setPurchaseData({...purchaseData, loanTerm: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="propertyTax">Annual Property Tax</Label>
                        <Input
                          id="propertyTax"
                          type="number"
                          placeholder="6000"
                          value={purchaseData.propertyTax}
                          onChange={(e) => setPurchaseData({...purchaseData, propertyTax: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="homeInsurance">Annual Home Insurance</Label>
                        <Input
                          id="homeInsurance"
                          type="number"
                          placeholder="1200"
                          value={purchaseData.homeInsurance}
                          onChange={(e) => setPurchaseData({...purchaseData, homeInsurance: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pmi">Monthly PMI</Label>
                        <Input
                          id="pmi"
                          type="number"
                          placeholder="200"
                          value={purchaseData.pmi}
                          onChange={(e) => setPurchaseData({...purchaseData, pmi: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="hoaFees">Monthly HOA Fees</Label>
                        <Input
                          id="hoaFees"
                          type="number"
                          placeholder="150"
                          value={purchaseData.hoaFees}
                          onChange={(e) => setPurchaseData({...purchaseData, hoaFees: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={calculatePurchase}
                      className="w-full bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                    >
                      Calculate Payment
                    </Button>
                  </CardContent>
                </Card>

                {/* Purchase Results */}
                <Card className="border-none bg-white">
                  <CardHeader>
                    <CardTitle>Payment Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {purchaseResults ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-[#0b2c4a]/10 rounded-lg">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-[#0b2c4a] mb-1">
                              ${purchaseResults.monthlyPITI}
                            </div>
                            <div className="text-sm text-gray-600">Total Monthly Payment (PITI)</div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Loan Amount:</span>
                            <span className="font-medium">${purchaseResults.loanAmount}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Principal & Interest:</span>
                            <span className="font-medium">${purchaseResults.monthlyPI}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Interest Paid:</span>
                            <span className="font-medium">${purchaseResults.totalInterest}</span>
                          </div>
                        </div>
                        
                        <Badge className="w-full justify-center bg-green-100 text-green-800 hover:bg-green-200">
                          Estimate Only - Rates Subject to Change
                        </Badge>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        Enter your information and click "Calculate Payment" to see your results.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Refinance Calculator */}
            <TabsContent value="refinance">
              <div className="grid lg:grid-cols-2 gap-8">
                <Card className="border-none bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <RefreshCw className="h-5 w-5 text-[#0b2c4a]" />
                      <span>Refinance Calculator</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="currentPayment">Current PITI Payment</Label>
                        <Input
                          id="currentPayment"
                          type="number"
                          placeholder="2500"
                          value={refinanceData.currentPayment}
                          onChange={(e) => setRefinanceData({...refinanceData, currentPayment: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="currentRate">Current Rate (%)</Label>
                        <Input
                          id="currentRate"
                          type="number"
                          step="0.01"
                          placeholder="7.5"
                          value={refinanceData.currentRate}
                          onChange={(e) => setRefinanceData({...refinanceData, currentRate: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="newRate">New Interest Rate (%)</Label>
                        <Input
                          id="newRate"
                          type="number"
                          step="0.01"
                          placeholder="6.0"
                          value={refinanceData.newRate}
                          onChange={(e) => setRefinanceData({...refinanceData, newRate: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="newLoanAmount">New Loan Amount</Label>
                        <Input
                          id="newLoanAmount"
                          type="number"
                          placeholder="400000"
                          value={refinanceData.newLoanAmount}
                          onChange={(e) => setRefinanceData({...refinanceData, newLoanAmount: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="newLoanTerm">New Loan Term (Years)</Label>
                        <Input
                          id="newLoanTerm"
                          type="number"
                          placeholder="30"
                          value={refinanceData.newLoanTerm}
                          onChange={(e) => setRefinanceData({...refinanceData, newLoanTerm: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="refinancePropertyTax">Annual Property Tax</Label>
                        <Input
                          id="refinancePropertyTax"
                          type="number"
                          placeholder="6000"
                          value={refinanceData.propertyTax}
                          onChange={(e) => setRefinanceData({...refinanceData, propertyTax: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="refinanceHomeInsurance">Annual Home Insurance</Label>
                        <Input
                          id="refinanceHomeInsurance"
                          type="number"
                          placeholder="1200"
                          value={refinanceData.homeInsurance}
                          onChange={(e) => setRefinanceData({...refinanceData, homeInsurance: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="refinanceHoaFees">Monthly HOA Fees</Label>
                        <Input
                          id="refinanceHoaFees"
                          type="number"
                          placeholder="150"
                          value={refinanceData.hoaFees}
                          onChange={(e) => setRefinanceData({...refinanceData, hoaFees: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={calculateRefinance}
                      className="w-full bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                    >
                      Calculate Savings
                    </Button>
                  </CardContent>
                </Card>

                {/* Refinance Results */}
                <Card className="border-none bg-white">
                  <CardHeader>
                    <CardTitle>Savings Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {refinanceResults ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-green-600 mb-1">
                              ${refinanceResults.monthlySavings}
                            </div>
                            <div className="text-sm text-gray-600">Monthly Savings</div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">New Monthly Payment:</span>
                            <span className="font-medium">${refinanceResults.newTotalPayment}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">New P&I Payment:</span>
                            <span className="font-medium">${refinanceResults.newMonthlyPI}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Annual Savings:</span>
                            <span className="font-medium text-green-600">${refinanceResults.annualSavings}</span>
                          </div>
                        </div>
                        
                        <Badge className="w-full justify-center bg-green-100 text-green-800 hover:bg-green-200">
                          Potential Savings - Contact Us for Actual Rates
                        </Badge>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        Enter your current and new loan information to see potential savings.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Move Forward?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            These calculations are estimates. Contact our experienced loan advisors 
            for personalized rates and to explore your options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => window.location.href = 'tel:(720) 650-7988'}
            >
              Call (720) 650-7988
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
              asChild
            >
              <a href="/team">Meet Our Team</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculators;